/**
 * Done. / ChiroCPD — Copy Field Context
 *
 * Tracks which export field was most recently copied, enabling:
 *   - Visual "copied" feedback across multiple CopyButton instances
 *   - Screen reader announcements ("Annual Plan Goal copied")
 *   - Audit trail of copy events (no PII — fieldName only)
 *
 * Resets automatically after COPY_REVERT_MS (2000ms).
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { COPY_REVERT_MS } from '@/lib/types';

// ── Context shape ─────────────────────────────────────────────────────────────

interface CopyFieldContextValue {
  /** The fieldName of the most recently copied field, or null */
  copiedField: string | null;
  /** Call this when a copy succeeds to register the field and start revert timer */
  registerCopy: (fieldName: string) => void;
  /** Call this to clear the copied state immediately (e.g. on error) */
  clearCopy: () => void;
}

const CopyFieldContext = createContext<CopyFieldContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────────

interface CopyFieldProviderProps {
  children: ReactNode;
}

export function CopyFieldProvider({ children }: CopyFieldProviderProps): React.ReactElement {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCopy = useCallback((): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setCopiedField(null);
  }, []);

  const registerCopy = useCallback((fieldName: string): void => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setCopiedField(fieldName);
    timerRef.current = setTimeout(() => {
      setCopiedField(null);
      timerRef.current = null;
    }, COPY_REVERT_MS);
  }, []);

  const value: CopyFieldContextValue = {
    copiedField,
    registerCopy,
    clearCopy,
  };

  return (
    <CopyFieldContext.Provider value={value}>{children}</CopyFieldContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useCopyFieldContext(): CopyFieldContextValue {
  const ctx = useContext(CopyFieldContext);
  if (!ctx) {
    throw new Error('useCopyFieldContext must be used within a CopyFieldProvider');
  }
  return ctx;
}
